/**
 * Netflix-Style Next Test Recommendation Service
 * Analyzes results by topic/category with weighted recent mistakes
 * Identifies weakest topic and recommends next test
 */

import { aiCoach, TestResult } from './aiCoach';

// Test metadata with categories
const TEST_METADATA: Record<string, { name: string; category: string }> = {
  'traffic-lights-signals': { name: 'Traffic Lights & Signals', category: 'signals' },
  'priority-rules': { name: 'Priority & Right of Way', category: 'rules' },
  'hazard-perception': { name: 'Hazard Perception', category: 'safety' },
  'speed-safety': { name: 'Speed & Safety', category: 'safety' },
  'bicycle-interactions': { name: 'Bicycle Interactions', category: 'interactions' },
  'roundabout-rules': { name: 'Roundabout Rules', category: 'rules' },
  'tram-interactions': { name: 'Tram Interactions', category: 'interactions' },
  'pedestrian-crossings': { name: 'Pedestrian Crossings', category: 'interactions' },
  'construction-zones': { name: 'Construction Zones', category: 'zones' },
  'weather-conditions': { name: 'Weather Conditions', category: 'safety' },
  'road-signs': { name: 'Road Signs', category: 'signs' },
  'mandatory-signs': { name: 'Mandatory Signs', category: 'signs' },
  'prohibitory-signs': { name: 'Prohibitory Signs', category: 'signs' },
  'prohibitory-signs-2': { name: 'Prohibitory Signs 2', category: 'signs' },
  'warning-signs': { name: 'Warning Signs', category: 'signs' },
  'road-information': { name: 'Road Information', category: 'signs' },
  'motorway-rules': { name: 'Motorway Rules', category: 'rules' },
  'vehicle-knowledge': { name: 'Vehicle Knowledge', category: 'vehicles' },
  'parking-rules': { name: 'Parking Rules', category: 'rules' },
  'environmental': { name: 'Environmental Zones', category: 'zones' },
  'technology-safety': { name: 'Technology & Safety', category: 'technology' },
  'alcohol-drugs': { name: 'Alcohol & Drugs', category: 'safety' },
  'fatigue-rest': { name: 'Fatigue & Rest', category: 'safety' },
  'emergency-procedures': { name: 'Emergency Procedures', category: 'safety' },
  'insight-practice': { name: 'Insight Practice', category: 'advanced' },
};

interface TopicAnalysis {
  category: string;
  accuracy: number;
  weightedAccuracy: number; // Recent mistakes weighted higher
  testCount: number;
  recentMistakes: number;
  testIds: string[];
}

class NetflixStyleRecommendationService {
  /**
   * Analyze results by topic/category with weighted recent mistakes
   */
  analyzeByTopic(testHistory: TestResult[]): Map<string, TopicAnalysis> {
    const topicMap = new Map<string, TopicAnalysis>();

    // Group tests by category
    testHistory.forEach((result, index) => {
      const testMeta = TEST_METADATA[result.testId];
      if (!testMeta) return;

      const category = testMeta.category;
      const isRecent = index >= testHistory.length - 3; // Last 3 tests are "recent"
      const weight = isRecent ? 2.0 : 1.0; // Recent tests weighted 2x

      if (!topicMap.has(category)) {
        topicMap.set(category, {
          category,
          accuracy: 0,
          weightedAccuracy: 0,
          testCount: 0,
          recentMistakes: 0,
          testIds: []
        });
      }

      const topic = topicMap.get(category)!;
      topic.testIds.push(result.testId);
      topic.testCount += 1;
      
      // Track accuracy (percentage)
      const currentAccuracy = result.percentage;
      topic.accuracy = (topic.accuracy * (topic.testCount - 1) + currentAccuracy) / topic.testCount;
      
      // Weighted accuracy (recent mistakes weighted higher)
      const weightedSum = topic.weightedAccuracy * (topic.testCount - 1) + (currentAccuracy * weight);
      topic.weightedAccuracy = weightedSum / (topic.testCount - 1 + weight);
      
      // Track recent mistakes (low scores in recent tests)
      if (isRecent && currentAccuracy < 60) {
        topic.recentMistakes += 1;
      }
    });

    return topicMap;
  }

  /**
   * Identify weakest topic based on:
   * - Lowest weighted accuracy
   * - Recent repeated mistakes
   * - Adaptive: avoid consecutive same topic; prioritize topics not addressed recently
   */
  identifyWeakestTopic(
    testHistory: TestResult[],
    options?: { excludeCategory?: string; lastCompletedCategories?: string[] }
  ): string | null {
    const topicAnalysis = this.analyzeByTopic(testHistory);
    if (topicAnalysis.size === 0) return null;

    const excludeCategory = options?.excludeCategory;
    const lastCompleted = options?.lastCompletedCategories ?? [];

    const topics = Array.from(topicAnalysis.values());
    topics.sort((a, b) => {
      // Penalize excluded category (avoid consecutive same topic)
      if (excludeCategory) {
        if (a.category === excludeCategory && b.category !== excludeCategory) return 1;
        if (b.category === excludeCategory && a.category !== excludeCategory) return -1;
      }
      // Boost topics not addressed in last 1–2 tests (prioritize not-recently-addressed)
      const aRecentlyAddressed = lastCompleted.indexOf(a.category) >= 0;
      const bRecentlyAddressed = lastCompleted.indexOf(b.category) >= 0;
      if (aRecentlyAddressed && !bRecentlyAddressed) return 1;
      if (!aRecentlyAddressed && bRecentlyAddressed) return -1;
      // Primary: weighted accuracy (lower = weaker)
      if (Math.abs(a.weightedAccuracy - b.weightedAccuracy) > 1) {
        return a.weightedAccuracy - b.weightedAccuracy;
      }
      if (a.recentMistakes !== b.recentMistakes) {
        return b.recentMistakes - a.recentMistakes;
      }
      return a.accuracy - b.accuracy;
    });

    return topics[0]?.category || null;
  }

  /**
   * Get next test focused on weakest topic
   * Netflix-style: Automatically loads next best test
   */
  getNextTest(
    currentTestId: string,
    translate?: (key: string) => string
  ): { id: string; name: string } | null {
    const testHistory = aiCoach.getTestHistory();
    const testScores = aiCoach.getTestScores();
    const completedCount = Object.keys(testScores).length;

    // First 5 tests: no repeat – use strict FIRST_FIVE_IN_ORDER (aiCoach handles this)
    if (completedCount < 5) {
      const recommendation = aiCoach.getTopRecommendation(translate);
      if (recommendation.testId && recommendation.testId !== currentTestId && recommendation.testId !== 'mock-exam') {
        return { id: recommendation.testId, name: recommendation.testName };
      }
      return null;
    }

    // Analyze by topic
    const weakestCategory = this.identifyWeakestTopic(testHistory);
    
    // Get all tests in weakest category
    const categoryTests = Object.keys(TEST_METADATA).filter(testId => {
      const meta = TEST_METADATA[testId];
      return meta && meta.category === weakestCategory;
    });

    if (categoryTests.length === 0) {
      // Fallback to AI recommendation
      const recommendation = aiCoach.getTopRecommendation(translate);
      return {
        id: recommendation.testId,
        name: recommendation.testName
      };
    }

    // Filter out current test AND tests user has already taken (no repeats from 6th test onward)
    const takenTestIds = Array.from(new Set([
      ...testHistory.map(result => result.testId),
      currentTestId // current test counts as taken so we never recommend it as "next"
    ]));
    const availableTests = categoryTests.filter(id =>
      id !== currentTestId &&
      id !== 'mock-exam' &&
      !takenTestIds.includes(id)
    );
    
    if (availableTests.length === 0) {
      // If all tests in category are taken, find any untaken test
      const allUntakenTests = Object.keys(TEST_METADATA).filter(id => 
        id !== currentTestId && 
        id !== 'mock-exam' &&
        !takenTestIds.includes(id)
      );
      
      if (allUntakenTests.length === 0) {
        // All tests taken – allow retake via AI recommendation
        const recommendation = aiCoach.getTopRecommendation(translate);
        if (recommendation.testId && recommendation.testId !== currentTestId && recommendation.testId !== 'mock-exam') {
          return {
            id: recommendation.testId,
            name: recommendation.testName
          };
        }
        return null;
      }
      
      // Use untaken tests, sorted by score
      allUntakenTests.sort((a, b) => {
        const scoreA = testScores[a]?.average || 0;
        const scoreB = testScores[b]?.average || 0;
        if (scoreA === 0 && scoreB > 0) return -1;
        if (scoreA > 0 && scoreB === 0) return 1;
        return scoreA - scoreB;
      });
      
      const nextTestId = allUntakenTests[0];
      const testName = translate 
        ? this.getTranslatedTestName(nextTestId, translate)
        : TEST_METADATA[nextTestId].name;
      
      return {
        id: nextTestId,
        name: testName
      };
    }

    // Sort by score (lowest first = weakest)
    availableTests.sort((a, b) => {
      const scoreA = testScores[a]?.average || 0;
      const scoreB = testScores[b]?.average || 0;
      
      // Unpracticed tests (score = 0) come first
      if (scoreA === 0 && scoreB > 0) return -1;
      if (scoreA > 0 && scoreB === 0) return 1;
      
      // Then by score (lower = weaker = higher priority)
      return scoreA - scoreB;
    });

    const nextTestId = availableTests[0];
    const testName = translate 
      ? this.getTranslatedTestName(nextTestId, translate)
      : TEST_METADATA[nextTestId].name;

    return {
      id: nextTestId,
      name: testName
    };
  }

  /**
   * Get translated test name
   */
  private getTranslatedTestName(testId: string, translate: (key: string) => string): string {
    const testNameMap: Record<string, string> = {
      'traffic-lights-signals': translate('testNames.trafficLightsSignals'),
      'priority-rules': translate('testNames.priorityRules'),
      'hazard-perception': translate('testNames.hazardPerception'),
      'speed-safety': translate('testNames.speedSafety'),
      'bicycle-interactions': translate('testNames.bicycleInteractions'),
      'roundabout-rules': translate('testNames.roundaboutRules'),
      'tram-interactions': translate('testNames.tramInteractions'),
      'pedestrian-crossings': translate('testNames.pedestrianCrossings'),
      'construction-zones': translate('testNames.constructionZones'),
      'weather-conditions': translate('testNames.weatherConditions'),
      'road-signs': translate('testNames.roadSigns'),
      'mandatory-signs': translate('testNames.mandatorySigns'),
      'prohibitory-signs': translate('testNames.prohibitorySigns'),
      'prohibitory-signs-2': translate('testNames.prohibitorySigns2'),
      'warning-signs': translate('testNames.warningSigns'),
      'road-information': translate('testNames.roadInformation'),
      'motorway-rules': translate('testNames.motorwayRules'),
      'vehicle-knowledge': translate('testNames.vehicleKnowledge'),
      'parking-rules': translate('testNames.parkingRules'),
      'environmental': translate('testNames.environmental'),
      'technology-safety': translate('testNames.technologySafety'),
      'alcohol-drugs': translate('testNames.alcoholDrugs'),
      'fatigue-rest': translate('testNames.fatigueRest'),
      'emergency-procedures': translate('testNames.emergencyProcedures'),
      'insight-practice': translate('testNames.insightPractice'),
    };
    return testNameMap[testId] || TEST_METADATA[testId]?.name || testId;
  }

  /**
   * Precompute next test while user views results
   * This ensures instant loading when Next Test is clicked
   */
  precomputeNextTest(
    currentTestId: string,
    translate?: (key: string) => string
  ): { id: string; name: string } | null {
    // Use microtask to compute after render
    return this.getNextTest(currentTestId, translate);
  }
}

export const netflixStyleRecommendation = new NetflixStyleRecommendationService();

